// Type-only import: `class/Device` bundles Worker code that does not run in a test
// environment, and only the `DEVICE_STATUS` key set is needed here.
import type { DEVICE_STATUS } from '../../class/Device';

/** Park status as indexed by `dataGraph.statusArray` (index as a string). */
export type ParkStatusState = '0' | '1' | '2' | '3';

/** Status name, which also serves as the suffix of its translation key. */
export type ParkStatusName = 'MOVE' | 'STOP' | 'FULLY_STOPPED' | 'INACTIVE';

/** One of the four statuses displayed by the "Park state" cards. */
export interface ParkStatusDescriptor {
  /** Index in `dataGraph.statusArray`, emitted on a card click. */
  state: ParkStatusState;
  name: ParkStatusName;
  /** Matching value of `filtersFormGroup.statusFilter` (see `DevicesManagerService.setStatusDevice`). */
  movingStatus: keyof typeof DEVICE_STATUS;
  labelKey: `DEVICE.STATUS.${ParkStatusName}`;
}

/**
 * Single source of the `state` ↔ `movingStatus` mapping of the park status cards,
 * in their display order (from the most active to the least active).
 *
 * The colours are deliberately kept out of this table: `park-kpi` and the
 * `distribution-bar` do not use the same tint for the inactive status.
 */
export const PARK_STATUSES: readonly ParkStatusDescriptor[] = [
  { state: '3', name: 'MOVE', movingStatus: 'moving', labelKey: 'DEVICE.STATUS.MOVE' },
  { state: '2', name: 'STOP', movingStatus: 'stopped', labelKey: 'DEVICE.STATUS.STOP' },
  {
    state: '1',
    name: 'FULLY_STOPPED',
    movingStatus: 'fullyStopped',
    labelKey: 'DEVICE.STATUS.FULLY_STOPPED',
  },
  { state: '0', name: 'INACTIVE', movingStatus: 'inactive', labelKey: 'DEVICE.STATUS.INACTIVE' },
] as const;

/** `movingStatus` matching a card `state`, or `null` when the state is unknown. */
export function movingStatusFromState(state: string): keyof typeof DEVICE_STATUS | null {
  return PARK_STATUSES.find(status => status.state === state)?.movingStatus ?? null;
}

/** Card `state` matching a `movingStatus`, or `null` when the status is unknown. */
export function stateFromMovingStatus(movingStatus: keyof typeof DEVICE_STATUS): ParkStatusState | null {
  return PARK_STATUSES.find(status => status.movingStatus === movingStatus)?.state ?? null;
}
